import dayjs from "dayjs";

// LF360-2030: what the search widget asks the backend for, kept apart from the
// widget so it can be checked without rendering one.

/** The backend refuses shorter keywords: they cannot use its trigram index. */
export const MIN_QUERY_LENGTH = 3;

export const PAGE_SIZE = 20;

export const SORTS = [
  { key: "relevance", label: "Best match", hint: "Title starts with your search first" },
  { key: "updated_desc", label: "Recently updated", hint: "Newest first" },
  { key: "updated_asc", label: "Oldest updated", hint: "Find stale content" },
  { key: "title_asc", label: "Title A–Z", hint: "Alphabetical" },
];

/**
 * The "Updated" presets. Their starts are worked out here, in the admin's own
 * time zone — "today" is their today — and sent as instants: the backend does
 * not guess a zone.
 */
export const DATE_PRESETS = [
  { key: "today", label: "Today", from: (now) => now.startOf("day") },
  { key: "7d", label: "Past 7 days", from: (now) => now.subtract(7, "day") },
  { key: "30d", label: "Past 30 days", from: (now) => now.subtract(30, "day") },
  { key: "3m", label: "Past 3 months", from: (now) => now.subtract(3, "month") },
  { key: "1y", label: "Past year", from: (now) => now.subtract(1, "year") },
];

export const CUSTOM_RANGE = "custom";

/** The usable keyword: trimmed, and nothing until it is long enough. */
export const effectiveQuery = (query) => {
  const q = (query || "").trim();

  return q.length >= MIN_QUERY_LENGTH ? q : "";
};

/** "Best match" needs something to match; without a keyword it is the newest. */
export const effectiveSort = (sort, query) => {
  const hasQuery = Boolean(effectiveQuery(query));

  if (!sort) {return hasQuery ? "relevance" : "updated_desc";}
  if (sort === "relevance" && !hasQuery) {return "updated_desc";}

  return sort;
};

/**
 * The update range a date choice stands for. A custom range runs from the
 * start of its first day to the end of its last, in the admin's zone.
 */
export const updatedRange = (date, now = dayjs()) => {
  if (!date) {return {};}

  if (date.preset === CUSTOM_RANGE) {
    return {
      ...(date.from ? { updatedFrom: dayjs(date.from).startOf("day").toISOString() } : {}),
      ...(date.to ? { updatedTo: dayjs(date.to).endOf("day").toISOString() } : {}),
    };
  }

  const preset = DATE_PRESETS.find((p) => p.key === date.preset);

  return preset ? { updatedFrom: preset.from(now).toISOString() } : {};
};

/**
 * Every chosen value of the type's filters, under the parameter the backend
 * reads it from. Nothing chosen means "any". No status is ever sent: the
 * backend then searches switched-on content only, which is all that is offered.
 */
const chosenFilters = (filters = {}, filterDefs = []) =>
  Object.fromEntries(
    filterDefs
      .map((def) => [def.key, filters[def.key] || []])
      .filter(([, values]) => values.length)
  );

/** The query for a page of results. */
export const searchQuery = ({ query, filters, date, sort, cursor }, filterDefs, now = dayjs()) => ({
  ...(effectiveQuery(query) ? { q: effectiveQuery(query) } : {}),
  ...chosenFilters(filters, filterDefs),
  ...updatedRange(date, now),
  sort: effectiveSort(sort, query),
  limit: PAGE_SIZE,
  ...(cursor ? { cursor } : {}),
});

/** The query for the counts: the same filters, and where each preset starts. */
export const facetsQuery = ({ query, filters, date }, filterDefs, now = dayjs()) => ({
  ...(effectiveQuery(query) ? { q: effectiveQuery(query) } : {}),
  ...chosenFilters(filters, filterDefs),
  ...updatedRange(date, now),
  presets: DATE_PRESETS.map((preset) => preset.from(now).toISOString()),
});

const formatDay = (value) => dayjs(value).format("MMM D, YYYY");

/** How a date choice reads on its button and its chip. */
export const dateLabel = (date) => {
  if (!date) {return "";}
  if (date.preset !== CUSTOM_RANGE) {
    return DATE_PRESETS.find((p) => p.key === date.preset)?.label || "";
  }
  if (date.from && date.to) {return `${formatDay(date.from)} – ${formatDay(date.to)}`;}
  if (date.from) {return `After ${formatDay(date.from)}`;}
  if (date.to) {return `Before ${formatDay(date.to)}`;}

  return "Custom range";
};

/**
 * The applied filters as removable chips: one per value, and one for the date.
 * A value whose name is not known (yet) shows as its id rather than vanishing.
 */
export const chipsOf = ({ filters = {}, date }, filterDefs, optionsByKey = {}) => {
  const chips = filterDefs.flatMap((def) =>
    (filters[def.key] || []).map((value) => ({
      key: `${def.key}:${value}`,
      dimension: def.label,
      filterKey: def.key,
      value,
      label: (optionsByKey[def.key] || []).find((option) => option.value === value)?.label || value,
    }))
  );

  if (date) {
    chips.push({ key: "updated", dimension: "Updated", filterKey: "updated", label: dateLabel(date) });
  }

  return chips;
};

/** A keyword's first match in a title, for highlighting: [before, match, after]. */
export const splitMatch = (title, query) => {
  const text = title || "";
  const q = effectiveQuery(query).toLowerCase();
  const at = q ? text.toLowerCase().indexOf(q) : -1;

  if (at < 0) {return [text, "", ""];}

  return [text.slice(0, at), text.slice(at, at + q.length), text.slice(at + q.length)];
};
